import React, { useState } from 'react';
import InputField from '../../input-field';
import AccountCheckbox from '../../account-checkbox';
import Button from '../../button';
import useStates from '../../../hooks/use-states';
import useCountries from '../../../hooks/use-countries';
import DropdownField from '../../dropdown-field';
import Link from '../../link';
import phoneFormat from '../../../lib/phone-format';
import AddressValidation from '../../address-validation';
import Address from '../../../types/address';
import TinInput from '../../tin-input';

const AccountSignupForm: React.FC = () => {
  // Account
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [tin, setTin] = useState('');

  // Address
  const [addressCompany, setAddressCompany] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [fax, setFax] = useState('');

  const [validAddress, setValidAddress] = useState(false);

  // Notifications
  const [notificationsNews, setNotificationsNews] = useState(false);
  const [notificationsMarketing, setNotificationsMarketing] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setProcessing] = useState(false);

  const states = useStates();
  const countries = useCountries();

  const mapCountryToOption = (country) => {
    return {
      label: country.name,
      value: country.code,
    };
  };

  const mapStateToOption = (state) => {
    return {
      label: state.name,
      value: state.stateCode,
    };
  };

  const renderStateField = () => {
    const stateOptions = states
      .filter((s) => s.countryCode === addressCountry)
      .map(mapStateToOption);

    return stateOptions.length ? (
      <DropdownField
        id="addressState"
        label="State"
        value={addressState}
        options={[{ label: '- Choose One -', value: '' }, ...stateOptions]}
        required={true}
        onChange={(e) => {
          setValidAddress(false);
          setAddressState(e.target.value);
        }}
      />
    ) : (
      <InputField
        id="addressState"
        label="State"
        type="text"
        defaultValue={addressState}
        required={true}
        onChange={(e) => {
          setValidAddress(false);
          setAddressState(e.target.value);
        }}
      />
    );
  };

  const countryOptions = countries
    .filter(
      (c) =>
        c.code === 'US' || c.code === 'CA' || c.code === 'PR' || c.code === 'SE'
    )
    .map(mapCountryToOption);

  const handleClick = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!username || !password) {
      setErrorMessage(
        'A username and password is required, but was not specified.'
      );
      setProcessing(false);
      return;
    }

    if (password !== passwordConfirm) {
      setErrorMessage('Your passwords do not match.');
      setProcessing(false);
      return;
    }

    if (!firstName || !lastName) {
      setErrorMessage(
        'Your first and last name are required, but one or both were not specified.'
      );
      setProcessing(false);
      return;
    }

    if (!email) {
      setErrorMessage('Your email address is required, but was not specified.');
      setProcessing(false);
      return;
    }

    if (
      !address1 ||
      !addressCity ||
      !addressState ||
      !addressPostalCode ||
      !addressCountry ||
      !phone
    ) {
      setErrorMessage(
        'All address details are required, but one or more details was not specified.'
      );
      setProcessing(false);
      return;
    }

    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    const phoneRegex2 = /^\d-\d{3}-\d{3}-\d{4}$/;

    // Ensure phone number matches the format
    if (!phoneRegex.test(phone) && !phoneRegex2.test(phone)) {
      setErrorMessage('Invalid phone number format.');
      setProcessing(false);
      return;
    }

    if (addressCompany.length > 50) {
      setErrorMessage('Company name cannot exceed 50 characters.');
      setProcessing(false);
    }

    // check email address
    const emailDomainParts = email.split('.');
    const topLevelDomain = emailDomainParts[emailDomainParts.length - 1];
    if (
      !['com', 'biz', 'gov', 'org', 'edu', 'co', 'net', 'ca'].some(
        (tld) => tld === topLevelDomain.toLowerCase()
      ) ||
      email.toLowerCase().includes('tetom77') ||
      email.toLowerCase().includes('admin')
    ) {
      setErrorMessage('Invalid email address.');
      setProcessing(false);
      return;
    }

    const bodyData = new URLSearchParams();
    // account
    bodyData.append('username', username);
    bodyData.append('password', password);
    bodyData.append('firstName', firstName);
    bodyData.append('lastName', lastName);
    bodyData.append('email', email);
    bodyData.append('tin', tin);
    // address
    bodyData.append('addressCompany', addressCompany);
    bodyData.append('address1', address1);
    bodyData.append('address2', address2);
    bodyData.append('addressCity', addressCity);
    bodyData.append('addressState', addressState);
    bodyData.append('addressPostalCode', addressPostalCode);
    bodyData.append('addressCountry', addressCountry);
    bodyData.append('phone', phone);
    bodyData.append('fax', fax);
    // notifications
    bodyData.append(
      'notificationsNews',
      notificationsNews as unknown as string
    );
    bodyData.append(
      'notificationsMarketing',
      notificationsMarketing as unknown as string
    );

    const res = await fetch('/api/signup', {
      method: 'post',
      body: bodyData.toString(),
    });

    if (res.ok) {
      const json = await res.json();
      setSuccessMessage(json?.message);
      setProcessing(false);

      // Clear form
      setUsername('');
      setPassword('');
      setPasswordConfirm('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setTin('');
      setAddressCompany('');
      setAddress1('');
      setAddress2('');
      setAddressCountry('');
      setAddressCity('');
      setAddressState('');
      setAddressPostalCode('');
      setPhone('');
      setFax('');
      setNotificationsNews(false);
      setNotificationsMarketing(false);
    } else {
      const json = await res.json();
      setErrorMessage(json?.error ?? 'An error occurred.');
      setProcessing(false);
    }
  };

  return (
    <div className="content">
      <form>
        <h2>Account</h2>
        <p>
          <strong>Notice:</strong> If you have been on this site before, please
          use your existing account information or{' '}
          <Link href="/account/forgot-password">
            <a>reset your password</a>
          </Link>
          .
        </p>
        <p className="mb-1">
          If you are a Volvo Retailer, please log in through the Retailer Portal
          and click on the Volvo Car Bookstore Link.
        </p>
        <p>
          <strong>Fields marked with an asterisk (*) are required.</strong>
          <br />
          <br />
        </p>
        <InputField
          id="username"
          label="Username"
          defaultValue={username}
          type="text"
          required={true}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
        />
        <InputField
          id="password"
          label="Password"
          defaultValue={password}
          type="password"
          required={true}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <InputField
          id="passwordConfirm"
          label="Confirm Password"
          defaultValue={passwordConfirm}
          type="password"
          required={true}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          autoComplete="new-password"
        />
        <InputField
          id="firstName"
          label="First Name"
          defaultValue={firstName}
          type="text"
          required={true}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <InputField
          id="lastName"
          label="Last Name"
          defaultValue={lastName}
          type="text"
          required={true}
          onChange={(e) => setLastName(e.target.value)}
        />
        <InputField
          id="email"
          label="Email"
          defaultValue={email}
          type="email"
          required={true}
          onChange={(e) => setEmail(e.target.value)}
        />
        <h2>Taxpayer Identification</h2>
        <TinInput tin={tin} onChange={setTin} />
        <h2>Address</h2>
        <InputField
          id="addressCompany"
          label="Company"
          maxLength={50}
          defaultValue={addressCompany}
          type="text"
          onChange={(e) => setAddressCompany(e.target.value)}
        />
        <p className="mb-1">
          This field is required for independent repair shop VIDA subscriptions.
        </p>
        <InputField
          id="address1"
          label="Address 1"
          defaultValue={address1}
          type="text"
          required={true}
          onChange={(e) => {
            setValidAddress(false);
            setAddress1(e.target.value);
          }}
        />
        <InputField
          id="address2"
          label="Address 2"
          defaultValue={address2}
          type="text"
          required={false}
          onChange={(e) => setAddress2(e.target.value)}
        />
        <DropdownField
          id="addressCountry"
          label="Country"
          value={addressCountry}
          options={[{ label: '- Choose One -', value: '' }, ...countryOptions]}
          required={true}
          onChange={(e) => {
            setValidAddress(false);
            setAddressCountry(e.target.value);
          }}
        />
        <InputField
          id="addressCity"
          label="City"
          defaultValue={addressCity}
          type="text"
          required={true}
          onChange={(e) => {
            setValidAddress(false);
            setAddressCity(e.target.value);
          }}
        />
        {renderStateField()}
        <InputField
          id="addressPostalCode"
          label="Postal Code"
          defaultValue={addressPostalCode}
          type="text"
          required={true}
          onChange={(e) => {
            setValidAddress(false);
            setAddressPostalCode(e.target.value);
          }}
        />
        <InputField
          id="phone"
          label="Phone"
          hint="Use the format 123-456-7890 or 1-123-456-7890 (with country code)."
          defaultValue={phone}
          type="tel"
          autoComplete="tel"
          required={true}
          maxLength={14}
          onChange={(e) => setPhone(phoneFormat(e.target.value))}
        />
        <InputField
          id="fax"
          label="Fax"
          defaultValue={fax}
          type="text"
          onChange={(e) => setFax(e.target.value)}
        />
        <AddressValidation
          address={{
            attention: '',
            company: addressCompany,
            address1,
            address2,
            county: '',
            city: addressCity,
            state: addressState,
            postalCode: addressPostalCode,
            country: addressCountry,
            phone: phone,
            email: email,
          }}
          updateAddress={(sa: Address) => {
            setAddressCountry(sa.country);
            setAddressState(sa.state);
            setAddressCity(sa.city);
            setAddressPostalCode(sa.postalCode);
            setAddress1(sa.address1);
            setAddress2(sa.address2);

            setValidAddress(true);
          }}
        />
        <h2>Mail preference</h2>
        <AccountCheckbox
          id="notificationsNews"
          label="Opt in to receive weekly notifications of What's New."
          checked={notificationsNews}
          onClick={() => setNotificationsNews(!notificationsNews)}
        />
        <AccountCheckbox
          id="notificationsMarketing"
          label="Opt in to receive periodic emails containing news and information."
          checked={notificationsMarketing}
          onClick={() => setNotificationsMarketing(!notificationsMarketing)}
        />
        {validAddress && (
          <>
            <Button
              text="Register"
              disabled={isProcessing}
              onClick={handleClick}
            />
            {errorMessage && <p className="errorMessage">{errorMessage}</p>}
            {successMessage && <p>{successMessage}</p>}
          </>
        )}
      </form>
      {/* language=CSS */}
      <style jsx>{`
        h2 {
          margin: 0 0 1rem;
          font-family: 'Volvo Novum', sans-serif;
          font-weight: 400;
        }

        p {
          font-family: 'Volvo Novum', sans-serif;
          font-size: 14px;
          margin: 0;
        }

        p.mb-1 {
          margin-bottom: 1rem;
        }

        a {
          color: #333;
          font-family: 'Volvo Novum', sans-serif;
          font-size: 14px;
        }

        a:hover {
          color: #707070;
        }

        .errorMessage {
          color: #ff2000;
        }
      `}</style>
    </div>
  );
};

export default AccountSignupForm;
